import { GoogleDocs } from 'iconoir-react';
import { useTranslation } from 'react-i18next';
import { cn } from 'lib/utils';

interface ProjectActionCardsProps {
    onAddInstruction: () => void;
    onOpenFiles: () => void;
    totalFiles: number;
}

function ActionCardDivider() {
    return (
        <>
            <div
                className="h-px w-full shrink-0 bg-grey-100 dark:bg-border sm:hidden"
                aria-hidden="true"
            />
            <div
                className="hidden h-20 w-px shrink-0 bg-grey-100 dark:bg-border sm:block"
                aria-hidden="true"
            />
        </>
    );
}

function ActionCardWrapper({
    children,
    className,
}: {
    children: React.ReactNode;
    className?: string;
}) {
    return (
        <div className={cn('flex min-w-0 flex-1 items-center self-stretch', className)}>
            {children}
        </div>
    );
}

function ActionCardDocIcon() {
    return (
        <span className="relative inline-flex size-6 shrink-0">
            <GoogleDocs
                width={24}
                height={24}
                strokeWidth={1.5}
                className="text-grey-700 dark:text-true-white"
            />
        </span>
    );
}

export function ProjectActionCards({
    onAddInstruction,
    onOpenFiles,
    totalFiles,
}: ProjectActionCardsProps) {
    const { t } = useTranslation();

    const cardClassName =
        'flex min-w-0 flex-1 flex-col items-start gap-2 rounded-lg py-4 text-left transition-colors hover:bg-grey-50 dark:hover:bg-white/5';

    const titleClassName =
        'shrink-0 whitespace-nowrap font-sans text-sm font-bold not-italic leading-[1.2] text-grey-700 dark:text-true-white';

    const subtitleClassName =
        'min-w-full font-sans text-sm font-normal not-italic leading-[1.2] text-grey-700 dark:text-true-white';

    return (
        <div className="flex w-full flex-col items-stretch gap-0 self-stretch rounded-lg border border-grey-50 bg-white px-4 py-[11px] dark:border-border dark:bg-background sm:flex-row sm:items-center sm:gap-6">
            <ActionCardWrapper>
                <button type="button" onClick={onAddInstruction} className={cardClassName}>
                    <div className="flex items-center gap-1">
                        <ActionCardDocIcon />
                        <span className={titleClassName}>
                            {t('INSTRUCTION') || 'Instructions'}
                        </span>
                    </div>
                    <span className={subtitleClassName}>
                        {t('CLICK_TO_ADD_INSTRUCTIONS') || 'Click to add instructions for the project'}
                    </span>
                </button>
            </ActionCardWrapper>

            <ActionCardDivider />

            <ActionCardWrapper>
                <button type="button" onClick={onOpenFiles} className={cardClassName}>
                    <div className="flex items-center gap-1">
                        <ActionCardDocIcon />
                        <span className={titleClassName}>
                            {t('ATTACHED_FILES') || 'Attached Files'}
                            {totalFiles > 0 ? ` (${totalFiles})` : ''}
                        </span>
                    </div>
                    <span className={subtitleClassName}>
                        {t('CLICK_TO_ADD_FILES') || 'Click to add files to this project'}
                    </span>
                </button>
            </ActionCardWrapper>

            <ActionCardDivider />

            <ActionCardWrapper>
                <div className={cn(cardClassName, 'cursor-default')}>
                    <div className="flex items-center gap-1">
                        <ActionCardDocIcon />
                        <span className={titleClassName}>
                            {t('SKILLS') || 'Skills'}
                        </span>
                    </div>
                    <span className={subtitleClassName}>
                        {t('CLICK_TO_ADD_SKILLS') || 'Click to add skills to this project'}
                    </span>
                </div>
            </ActionCardWrapper>
        </div>
    );
}
